// Per-kind proposal commit — shared by the floating Avery widget and any
// inline Avery surface (e.g. the zone Rates tab). Each branch GET/POSTs the
// relevant API and writes an audit stamp. Extracted verbatim from
// AveryWidget.executeProposal (pure code-motion): it now THROWS on failure and
// resolves on success, leaving the per-card status bookkeeping
// (pending/executing/done/error) to the caller.
import {
  AVERY_SERVER_APPLY_KIND_SET,
  REQUIRED_INVITE_FIELDS,
  type InviteRequest,
} from '@parkify/api-types'
import { logAudit } from '../../utils/audit'
import { type Proposal, isLegacyProposalKind } from './types'
import { loadRouteActions } from './loadRouteActions'
import { averyStamp, extendIdempotencyKey } from './helpers'
import { fmtMoney } from '../../utils/money'
import { resolveWithGuard } from '../../screens/support/resolveGuard'
import { readRefundBlock } from '../../utils/refundBlock'
import { callApi } from '../../config/apiBase'

/**
 * The signed-in operator committing the proposal — `useAuth().user`, narrowed
 * to what any branch actually needs.
 *
 * Avery PROPOSES; a person APPROVES, and the approver is who the action is
 * attributed to: writeAuditEvent takes the actor from this request's token, and
 * the averyStamp rides along as provenance rather than authorship. Branches
 * that name the actor in an outbound artifact (today: the invite email's
 * `invitedBy`) have to name that same person, so it's passed in explicitly
 * instead of being re-derived per call site.
 */
export interface ProposalOperator {
  email: string
  name?: string
}

// Kinds the Worker can apply SERVER-side (roadmap Wave-4 PR 16) when
// support:server_apply_enabled is on: safe, non-money config writes only. Money
// kinds (refund, extend_session) are deliberately ABSENT — they always run the
// client path — and the server endpoint hard-denies them regardless. Must match
// SERVER_APPLY_KINDS in parkify-pay/api/src/avery/apply.ts.
const SERVER_APPLY_KINDS = AVERY_SERVER_APPLY_KIND_SET

/**
 * Commit a confirmed Avery proposal. `cardKey` is a stable per-card identifier
 * (e.g. `${msgId}:${idx}`) used to derive the idempotency key for the
 * money-moving extend path, so a Retry — even after a reload — never produces a
 * second charge. `operator` is who approved it (see ProposalOperator). Throws on
 * any failure; the caller surfaces the message.
 */
export async function runProposal(
  p: Proposal,
  authHeaders: () => HeadersInit,
  cardKey: string,
  operator: ProposalOperator | null,
): Promise<void> {
  // Server-side apply (roadmap Wave-4 PR 16). When the proposal has a server
  // identity (the proposal store was on at propose time) AND it's one of the safe
  // config kinds, hand execution to the Worker: it re-validates the guardrails at
  // EXECUTE time against the live record, drift-refuses a stale snapshot,
  // serializes the read-modify-write per zone, and writes the authoritative v2
  // audit stamp itself. If the Worker says the path is disabled, refuse rather
  // than falling through to the older client-side read-modify-write path. That
  // fallback is race-prone and lacks execute-time drift/guard re-validation.
  // A real refusal (disabled / drift / guard / money-denylist / error) is
  // surfaced — never silently re-applied on the client.
  if (p.id && SERVER_APPLY_KINDS.has(p.kind)) {
    const res = await callApi(`/api/avery/proposals/${encodeURIComponent(p.id)}/execute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
    })
    if (res.ok) {
      const data = await res.json().catch(() => ({})) as { disabled?: boolean }
      // Applied server-side (incl. idempotent already-executed) → the server wrote
      // the stamp; do NOT run the client branch too (double-apply + double-stamp).
      if (!data.disabled) return
      // The proposal has a durable server identity, so it must use the
      // authoritative apply boundary. The client path is intentionally not a
      // safety fallback for stored proposals.
      throw new Error('Avery server apply is disabled; this proposal cannot be executed safely right now.')
    } else {
      throw new Error(await res.text())
    }
  }

  // Route-backed kinds (@parkify/api-types/avery-actions): one call to the real
  // console endpoint, built from the registry (routeActions.ts, loaded on
  // demand). No per-kind branch below. Bespoke kinds never wait on the import.
  if (!isLegacyProposalKind(p.kind)) {
    const routes = await loadRouteActions()
    if (routes.isAveryActionKind(p.kind)) {
      await routes.runRouteAction(p.kind, p, authHeaders, averyStamp)
      return
    }
  }

  if (p.kind === 'refund') {
    const sessRes = await callApi(`/api/sessions/list?limit=200`, { headers: authHeaders() as Record<string, string> })
    if (!sessRes.ok) throw new Error(`session lookup ${sessRes.status}`)
    const sessData = await sessRes.json() as { sessions: Array<{ txnId: string; stripeIntentId?: string; plate: string; zoneId: string; amountCents: number }> }
    const txnId = String(p.payload.txnId)
    const sess = sessData.sessions.find(s => s.txnId === txnId)
    if (!sess) throw new Error(`Session ${txnId} not found`)
    if (!sess.stripeIntentId) throw new Error('No Stripe intent — refund manually')
    // Optional PARTIAL amount (cents). Absent → full refund: omit `amount` so
    // Stripe reverses the whole remaining charge. The Worker + Stripe enforce
    // the ≤-remaining ceiling authoritatively; we guard the obvious over-refund
    // here only to give a clearer message than Stripe's generic rejection.
    const partialCents = typeof p.payload.amountCents === 'number' && Number.isFinite(p.payload.amountCents)
      ? p.payload.amountCents
      : undefined
    if (partialCents !== undefined && partialCents > sess.amountCents) {
      throw new Error(`Partial refund ${fmtMoney(partialCents)} exceeds the session charge ${fmtMoney(sess.amountCents)}.`)
    }
    const refundPayload: Record<string, unknown> = {
      intentId: sess.stripeIntentId, ...(partialCents !== undefined ? { amount: partialCents } : {}),
    }
    let res = await callApi(`/api/payments/refunds`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(refundPayload),
    })
    // Pre-flight block (409 coverage_gap) — nothing has moved yet. The operator
    // already approved this Avery proposal, but a coverage gap is a SEPARATE,
    // server-computed finding they haven't seen — surface it as a native
    // confirm (readable text, not raw JSON) rather than resending blind.
    let gapAcknowledged = false
    if (res.status === 409) {
      const block = await readRefundBlock(res)
      if (block.kind === 'confirm') {
        if (!window.confirm(`${block.message}\n\nRefund anyway?`)) {
          throw new Error('Refund cancelled — the coverage-gap warning was not accepted.')
        }
        gapAcknowledged = true
        res = await callApi(`/api/payments/refunds`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
          body: JSON.stringify({ ...refundPayload, [block.overrideField]: true }),
        })
      } else if (block.kind === 'escalate') {
        throw new Error(block.message)
      }
    }
    if (!res.ok) throw new Error(await res.text())
    // `amount` is the ACTUAL refunded cents (full OR partial). Guard against
    // a missing/non-finite value so the stamp never carries NaN.
    const data = await res.json() as { id: string; amount?: number; status: string }
    const refunded = typeof data.amount === 'number' && Number.isFinite(data.amount) ? data.amount : null
    void logAudit({
      action: 'session.refunded',
      resourceType: 'session', resourceId: sess.txnId,
      resourceName: `${sess.plate} · ${sess.zoneId}`,
      detail: `Refunded ${fmtMoney(refunded ?? sess.amountCents)} via Stripe (${data.id}) — proposed by Avery${
        gapAcknowledged ? ' · coverage gap acknowledged by operator' : ''
      }`,
      // delta = -refunded (a refund reduces net). before/after track the
      // session's net charged-minus-refunded; for a multi-charge (extended)
      // session this refunds one intent, so `after` is the session net after
      // THIS refund, not a per-charge remainder. Omit before/after if the
      // refunded amount is unknown (delta then null too).
      averyStamp: refunded !== null
        ? averyStamp('refund', { zoneId: sess.zoneId, before: sess.amountCents, after: sess.amountCents - refunded }, p)
        : averyStamp('refund', { zoneId: sess.zoneId }, p),
    }, authHeaders())
  } else if (p.kind === 'resolve_ticket') {
    const threadId = String(p.payload.threadId)
    // outcome (OMNI-3.5a) is OPTIONAL and purely additive: absent → the body
    // is byte-identical to before. When present it rides along in the same
    // POST so the ticket status record carries a short note on how it was
    // resolved (e.g. "refunded the overcharge") alongside the existing
    // replySent/refundIssued-style flags.
    const outcome = typeof p.payload.outcome === 'string' && p.payload.outcome ? p.payload.outcome : undefined
    // A phone caller who was never reached needs a reason first; the person
    // executing the proposal gives it, or the ticket stays open
    // (screens/support/resolveGuard.ts).
    let failed = null as string | null
    const guarded = await resolveWithGuard(threadId, new Date().toISOString(), {
      post: async body => {
        let res: Response
        try {
          res = await callApi(`/api/support/status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
            body: JSON.stringify(body),
          })
        } catch (e) {
          failed = e instanceof Error ? e.message : String(e)
          throw e
        }
        if (!res.ok && res.status !== 409) failed = await res.clone().text()
        return res
      },
      ask: message => window.prompt(message),
    }, outcome ? { outcome } : {})
    if (failed) throw new Error(failed)
    if (!guarded.ok) throw new Error('Not resolved: the phone caller has not been reached, and no reason was given.')
    void logAudit({
      action: 'ticket.resolved',
      resourceType: 'ticket', resourceId: threadId,
      resourceName: threadId,
      detail: [
        outcome ? `Resolved via Avery proposal — ${outcome}` : 'Resolved via Avery proposal',
        guarded.ack ? `without reaching the caller: ${guarded.ack}` : '',
      ].filter(Boolean).join('; '),
      averyStamp: averyStamp('resolve_ticket', undefined, p),
    }, authHeaders())
  } else if (p.kind === 'reopen_ticket') {
    const threadId = String(p.payload.threadId)
    const res = await callApi(`/api/support/status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify({ threadId, status: 'open' }),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'ticket.reopened',
      resourceType: 'ticket', resourceId: threadId,
      resourceName: threadId,
      detail: 'Reopened via Avery proposal',
      averyStamp: averyStamp('reopen_ticket', undefined, p),
    }, authHeaders())
  } else if (p.kind === 'escalate_ticket') {
    const threadId = String(p.payload.threadId)
    const res = await callApi(`/api/support/status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify({ threadId, status: 'escalated' }),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'ticket.escalated',
      resourceType: 'ticket', resourceId: threadId,
      resourceName: threadId,
      detail: 'Escalated via Avery proposal',
      averyStamp: averyStamp('escalate_ticket', undefined, p),
    }, authHeaders())
  } else if (p.kind === 'resync_plate') {
    const plate = String(p.payload.plate)
    const ticketId = typeof p.payload.ticketId === 'string' ? p.payload.ticketId : undefined
    const res = await callApi(`/api/support/plate-resync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify({ plate, ...(ticketId ? { ticketId } : {}) }),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'plate.resynced',
      resourceType: 'plate', resourceId: plate,
      resourceName: plate,
      detail: ticketId ? `Resynced via Avery proposal for ticket ${ticketId}` : 'Resynced via Avery proposal',
      averyStamp: averyStamp('resync_plate', undefined, p),
    }, authHeaders())
  } else if (p.kind === 'update_rates_base') {
    // Fetch current rates, swap the targeted tier's amountCents, POST back
    // as the full ZoneRates body (the endpoint expects a complete record,
    // not a patch).
    const zoneId = String(p.payload.zoneId)
    const tierId = String(p.payload.tierId)
    const newAmt = Number(p.payload.newAmountCents)
    const ratesRes = await callApi(`/api/rates?zoneId=${encodeURIComponent(zoneId)}`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!ratesRes.ok) throw new Error(`rates lookup ${ratesRes.status}`)
    const rates = await ratesRes.json() as {
      zoneId: string
      tiers?: Array<{ id: string; amountCents: number; type: string }>
      dailyMaxCents?: number
      monthlyPlans?: unknown[]
    } | null
    if (!rates) throw new Error('No rates found for this zone')
    const tier = rates.tiers?.find(t => t.id === tierId)
    if (!tier) throw new Error(`Tier ${tierId} not found on zone ${zoneId}`)
    const prevAmt = tier.amountCents
    const updated = {
      ...rates,
      tiers: (rates.tiers ?? []).map(t => t.id === tierId ? { ...t, amountCents: newAmt } : t),
    }
    const res = await callApi(`/api/rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(updated),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'rate.updated',
      resourceType: 'rate', resourceId: `${zoneId}:${tierId}`,
      resourceName: zoneId,
      detail: `${prevAmt} → ${newAmt} cents — applied via Avery proposal`,
      averyStamp: averyStamp('update_rates_base', { zoneId, before: prevAmt, after: newAmt, baseline: p.payload.baseline }, p),
    }, authHeaders())
  } else if (p.kind === 'update_rates_event') {
    const zoneId = String(p.payload.zoneId)
    const eventId = String(p.payload.eventId)
    const newAmt = Number(p.payload.newAmountCents)
    const listRes = await callApi(`/api/event-rates?zoneId=${encodeURIComponent(zoneId)}`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!listRes.ok) throw new Error(`event-rates lookup ${listRes.status}`)
    const list = await listRes.json() as Array<{ id: string; zoneId: string; amountCents: number; eventTitle: string; startDateTime: string; endDateTime: string }>
    const existing = list.find(r => r.id === eventId)
    if (!existing) throw new Error(`Event rate ${eventId} not found on zone ${zoneId}`)
    const prevAmt = existing.amountCents
    const updated = { ...existing, amountCents: newAmt }
    const res = await callApi(`/api/event-rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(updated),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'rate.event_updated',
      resourceType: 'rate.event', resourceId: `${zoneId}:${eventId}`,
      resourceName: existing.eventTitle ?? zoneId,
      detail: `${prevAmt} → ${newAmt} cents — applied via Avery proposal`,
      averyStamp: averyStamp('update_rates_event', { zoneId, before: prevAmt, after: newAmt, baseline: p.payload.baseline }, p),
    }, authHeaders())
  } else if (p.kind === 'add_zone_photo') {
    const zoneId = String(p.payload.zoneId)
    const photoUrl = String(p.payload.photoUrl)
    const zoneRes = await callApi(`/api/zones?id=${encodeURIComponent(zoneId)}`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!zoneRes.ok) throw new Error(`zone lookup ${zoneRes.status}`)
    const zone = await zoneRes.json() as { id: string; photos?: string[] } & Record<string, unknown>
    const photos = Array.from(new Set([...(zone.photos ?? []), photoUrl]))
    const res = await callApi(`/api/zones`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify({ ...zone, photos }),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'zone.photo_added',
      resourceType: 'zone', resourceId: zoneId,
      resourceName: String(p.payload.zoneName ?? zoneId),
      detail: `Added photo ${photoUrl} via Avery proposal`,
      averyStamp: averyStamp('add_zone_photo', { zoneId }, p),
    }, authHeaders())
  } else if (p.kind === 'update_zone_config') {
    const zoneId = String(p.payload.zoneId)
    // Read-merge — POST /api/zones is a FULL RECORD REPLACE (not a merge), so
    // spreading the full existing record first is what preserves `active`,
    // `payoutEntityId`, rates, coords, etc. Only the changed config fields
    // (never active/payoutEntityId — this tool's payload never carries them,
    // see propose_update_zone_config's input_schema) are overwritten on top.
    const zoneRes = await callApi(`/api/zones?id=${encodeURIComponent(zoneId)}`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!zoneRes.ok) throw new Error(`zone lookup ${zoneRes.status}`)
    const zone = await zoneRes.json() as Record<string, unknown>
    const { zoneId: _pZoneId, zoneName, ...changedFields } = p.payload
    const res = await callApi(`/api/zones`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify({ ...zone, ...changedFields }),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'zone.config_updated',
      resourceType: 'zone', resourceId: zoneId,
      resourceName: String(zoneName ?? zoneId),
      detail: `Updated ${Object.keys(changedFields).join(', ')} via Avery proposal`,
      averyStamp: averyStamp('update_zone_config', { zoneId }, p),
    }, authHeaders())
  } else if (p.kind === 'create_zone') {
    const name = String(p.payload.name ?? '')
    const { name: _name, ...rest } = p.payload
    // OMIT `id` entirely — upsertZone's `resolvedId ?? body.id ?? uid('zone')`
    // does NOT treat '' as nullish, so sending `id: ''` would write a broken
    // `zone:` key. Omitting it lets the server generate a real id via uid('zone').
    // `active: true` is correct HERE (and only here) — this is a create, and a
    // brand-new zone should be live by default.
    // New zones start as a Draft: off sale until ops tests and takes them live
    // (docs/ZONE-DRAFT-MODE.md).
    const newZone = { ...rest, name, active: false, draft: true }
    const res = await callApi(`/api/zones`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(newZone),
    })
    if (!res.ok) throw new Error(await res.text())
    const created = await res.json() as { id?: string }
    void logAudit({
      action: 'zone.created',
      resourceType: 'zone', resourceId: String(created.id ?? name),
      resourceName: name,
      detail: `Created zone "${name}" via Avery proposal`,
      averyStamp: averyStamp('create_zone', { zoneId: created.id }, p),
    }, authHeaders())
  } else if (p.kind === 'create_facility') {
    // A creation must not overwrite an existing same-name location and its
    // pricing terms. The server derives the id from the name and owner.
    const name = String(p.payload.name ?? '')
    const res = await callApi(`/api/facilities`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify({ ...p.payload, createOnly: true }),
    })
    if (!res.ok) throw new Error(await res.text())
    const created = await res.json() as { id?: string }
    void logAudit({
      action: 'facility.created',
      resourceType: 'facility', resourceId: String(created.id ?? name),
      resourceName: name,
      detail: `Created location "${name}" via Avery proposal`,
      averyStamp: averyStamp('create_facility', undefined, p),
    }, authHeaders())
  } else if (p.kind === 'upsert_validation') {
    // `code` is the identity. Reuse edits a live code ONLY when the proposal is
    // explicitly `mode: 'edit'`; anything else — including a proposal stored
    // before `mode` existed — is sent `createOnly`, so the Worker answers 409
    // instead of overwriting the code (#3859 follow-up). Reconstruct the
    // nested inventory config from the flattened inventoryCap/inventoryReset;
    // the server tenant-scopes + zone-checks the write. Discount safety was
    // enforced at propose time (guardrails.ts checkValidationSafety).
    const { inventoryCap, inventoryReset, mode, ...rest } = p.payload
    const isEdit = mode === 'edit'
    const body: Record<string, unknown> = { ...rest, createOnly: !isEdit }
    if (inventoryCap != null) {
      body.inventory = { cap: Number(inventoryCap), reset: inventoryReset === 'calendar_month' ? 'calendar_month' : 'rolling_30d' }
    }
    const res = await callApi(`/api/validations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(body),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'validation.upserted',
      resourceType: 'validation', resourceId: String(p.payload.code ?? ''),
      resourceName: String(p.payload.name ?? p.payload.code ?? ''),
      detail: `${isEdit ? 'Edited' : 'Created'} validation ${String(p.payload.code ?? '')} via Avery proposal`,
      averyStamp: averyStamp('upsert_validation', {}, p),
    }, authHeaders())
  } else if (p.kind === 'delete_validation') {
    const code = String(p.payload.code ?? '')
    // Same as revoke_permit: the route's audit row carries the proposal stamp.
    const stamp = encodeURIComponent(JSON.stringify(averyStamp('delete_validation', {}, p)))
    const res = await callApi(`/api/validations?id=${encodeURIComponent(code)}&averyStamp=${stamp}`, {
      method: 'DELETE',
      headers: authHeaders() as Record<string, string>,
    })
    if (!res.ok) throw new Error(await res.text())
  } else if (p.kind === 'add_event') {
    // No id → server generates one; POST is an upsert.
    const newEvent = {
      zoneId: String(p.payload.zoneId),
      eventTitle: String(p.payload.eventTitle),
      startDateTime: String(p.payload.startDateTime),
      endDateTime: String(p.payload.endDateTime),
      amountCents: Number(p.payload.amountCents),
    }
    const res = await callApi(`/api/event-rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(newEvent),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'rate.event_added',
      resourceType: 'rate.event', resourceId: newEvent.zoneId,
      resourceName: newEvent.eventTitle,
      detail: `Added event window ${newEvent.startDateTime} → ${newEvent.endDateTime} @ ${newEvent.amountCents} cents via Avery proposal`,
      // Brand-new surge window — no prior comparable, so after-only (delta null).
      averyStamp: averyStamp('add_event', { zoneId: newEvent.zoneId, after: newEvent.amountCents }, p),
    }, authHeaders())
  } else if (p.kind === 'add_time_of_day_rate') {
    // Recurring time-of-day surge. The proposal carries 1-2 UTC tiers + the
    // id prefix. We drop any prior surge tiers with that prefix (idempotent
    // re-approve) and insert the new ones at the FRONT so checkout's .find()
    // selects the surge during its window and falls through to the base rate
    // outside it.
    const zoneId = String(p.payload.zoneId)
    const newTiers = (Array.isArray(p.payload.tiers) ? p.payload.tiers : []) as Array<{ id: string }>
    const idPrefix = String(p.payload.tierIdPrefix)
    if (newTiers.length === 0 || !idPrefix) throw new Error('Malformed surge proposal')
    const ratesRes = await callApi(`/api/rates?zoneId=${encodeURIComponent(zoneId)}`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!ratesRes.ok) throw new Error(`rates lookup ${ratesRes.status}`)
    const rates = await ratesRes.json() as {
      zoneId: string
      tiers?: Array<{ id: string } & Record<string, unknown>>
    } | null
    if (!rates) throw new Error('No rates found for this zone')
    const kept = (rates.tiers ?? []).filter(t => !String(t.id).startsWith(idPrefix))
    const updated = { ...rates, tiers: [...newTiers, ...kept] }
    const res = await callApi(`/api/rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(updated),
    })
    if (!res.ok) throw new Error(await res.text())
    const win = (p.payload.etWindow ?? {}) as { from?: string; to?: string }
    void logAudit({
      action: 'rate.tod_surge_added',
      resourceType: 'rate', resourceId: zoneId,
      resourceName: String(p.payload.zoneName ?? zoneId),
      detail: `Time-of-day surge ${win.from ?? '?'}–${win.to ?? '?'} ET @ ${Number(p.payload.surgeAmountCents)} cents (base ${Number(p.payload.baseAmountCents)}) via Avery proposal`,
      averyStamp: averyStamp('add_time_of_day_rate', {
        zoneId,
        before: Number(p.payload.baseAmountCents),
        after: Number(p.payload.surgeAmountCents),
        baseline: p.payload.baseline,
      }, p),
    }, authHeaders())
  } else if (p.kind === 'set_day_of_week_rate') {
    // Day-scoped rate override. Replacement is by EXACT tier id (this day set +
    // window only), so other day rules on the zone survive — that's what lets
    // Saturday and Sunday hold different prices. Ordering mirrors the Worker's
    // spliceDayOfWeekTier: a day+window tier is the narrowest match and goes to
    // the front; an all-day day tier sits after any windowed tiers (so an
    // existing every-day surge still wins inside its window) but ahead of the
    // unrestricted base. (Normally this executes SERVER-side — it's in
    // SERVER_APPLY_KINDS — so this branch only runs when server apply is off.)
    const zoneId = String(p.payload.zoneId)
    const tier = p.payload.tier as ({ id: string; timeFrom?: string } & Record<string, unknown>) | undefined
    if (!tier?.id) throw new Error('Malformed day-of-week rate proposal')
    const ratesRes = await callApi(`/api/rates?zoneId=${encodeURIComponent(zoneId)}`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!ratesRes.ok) throw new Error(`rates lookup ${ratesRes.status}`)
    const rates = await ratesRes.json() as {
      zoneId: string
      tiers?: Array<{ id: string; timeFrom?: string } & Record<string, unknown>>
    } | null
    if (!rates) throw new Error('No rates found for this zone')
    const kept = (rates.tiers ?? []).filter(t => t.id !== tier.id)
    const tiers = tier.timeFrom
      ? [tier, ...kept]
      : [...kept.filter(t => t.timeFrom), tier, ...kept.filter(t => !t.timeFrom)]
    const res = await callApi(`/api/rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify({ ...rates, tiers }),
    })
    if (!res.ok) throw new Error(await res.text())
    const win = p.payload.window as { from?: string; to?: string } | null | undefined
    void logAudit({
      action: 'rate.day_of_week_set',
      resourceType: 'rate', resourceId: `${zoneId}:${tier.id}`,
      resourceName: String(p.payload.zoneName ?? zoneId),
      detail: `Day-of-week rate ${String(p.payload.daysLabel ?? '')}${win?.from ? ` ${win.from}–${win.to}` : ''} @ ${Number(p.payload.amountCents)} cents (base ${Number(p.payload.baseAmountCents)}) via Avery proposal`,
      averyStamp: averyStamp('set_day_of_week_rate', {
        zoneId,
        before: Number(p.payload.baseAmountCents),
        after: Number(p.payload.amountCents),
        baseline: p.payload.baseline,
      }, p),
    }, authHeaders())
  } else if (p.kind === 'set_pass') {
    // Upsert ONE pass by id, leaving the zone's other passes alone — so an early
    // bird, a night pass and a weekly can coexist. Mirrors the Worker's
    // upsertPass: replace in place on update (the pass keeps its position in the
    // operator's list), append on create. (Normally this executes SERVER-side —
    // it's in SERVER_APPLY_KINDS — so this branch only runs when server apply is
    // off.)
    const zoneId = String(p.payload.zoneId)
    const pass = p.payload.pass as ({ id: string; label?: string } & Record<string, unknown>) | undefined
    if (!pass?.id) throw new Error('Malformed pass proposal')
    const ratesRes = await callApi(`/api/rates?zoneId=${encodeURIComponent(zoneId)}`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!ratesRes.ok) throw new Error(`rates lookup ${ratesRes.status}`)
    const rates = await ratesRes.json() as {
      zoneId: string
      passes?: Array<{ id: string } & Record<string, unknown>>
    } | null
    if (!rates) throw new Error('No rates found for this zone')
    const existing = rates.passes ?? []
    const idx = existing.findIndex(x => x.id === pass.id)
    const passes = idx === -1 ? [...existing, pass] : existing.map((x, i) => (i === idx ? pass : x))
    const res = await callApi(`/api/rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify({ ...rates, passes }),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'rate.pass_set',
      resourceType: 'rate', resourceId: `${zoneId}:pass_${pass.id}`,
      resourceName: String(p.payload.zoneName ?? zoneId),
      detail: `Pass "${String(pass.label ?? pass.id)}" @ ${Number(pass.amountCents)} cents via Avery proposal`,
      averyStamp: averyStamp('set_pass', {
        zoneId,
        before: typeof p.payload.priorAmountCents === 'number' ? p.payload.priorAmountCents : Number(pass.amountCents),
        after: Number(pass.amountCents),
        baseline: p.payload.baseline,
      }, p),
    }, authHeaders())
  } else if (p.kind === 'set_demand_tiers') {
    // Demand pricing is ONE ladder, not independent rules, so the proposal
    // replaces demandTiers wholesale (unlike passes / day rates, which upsert).
    const zoneId = String(p.payload.zoneId)
    const tiers = (Array.isArray(p.payload.tiers) ? p.payload.tiers : []) as Array<{ uptoCount: number; amountCents: number }>
    if (tiers.length === 0) throw new Error('Malformed demand-pricing proposal')
    const ratesRes = await callApi(`/api/rates?zoneId=${encodeURIComponent(zoneId)}`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!ratesRes.ok) throw new Error(`rates lookup ${ratesRes.status}`)
    const rates = await ratesRes.json() as { zoneId: string } & Record<string, unknown> | null
    if (!rates) throw new Error('No rates found for this zone')
    const res = await callApi(`/api/rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify({ ...rates, demandTiers: tiers }),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'rate.demand_tiers_set',
      resourceType: 'rate', resourceId: zoneId,
      resourceName: String(p.payload.zoneName ?? zoneId),
      detail: `Demand pricing set to ${tiers.length} occupancy bands (top ${Number(tiers[tiers.length - 1]?.amountCents)} cents) via Avery proposal`,
      averyStamp: averyStamp('set_demand_tiers', {
        zoneId,
        before: Number(tiers[0]?.amountCents),
        after: Number(tiers[tiers.length - 1]?.amountCents),
        baseline: p.payload.baseline,
      }, p),
    }, authHeaders())
  } else if (p.kind === 'remove_time_of_day_rate') {
    // One-tap UNDO of a time-of-day surge (roadmap Wave-4 PR 19). Client fallback
    // for when server apply is off — mirrors the Worker planKind: if the proposal
    // carries a `restoreTiers` snapshot, set the tiers back to it verbatim
    // (exact prior-state restore); otherwise drop every tier whose id starts with
    // the prefix. (Normally this executes SERVER-side — it's in SERVER_APPLY_KINDS
    // — so this branch only runs when support:server_apply_enabled is off.)
    const zoneId = String(p.payload.zoneId)
    const idPrefix = String(p.payload.tierIdPrefix ?? '')
    if (!idPrefix) throw new Error('Malformed remove-surge proposal')
    const ratesRes = await callApi(`/api/rates?zoneId=${encodeURIComponent(zoneId)}`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!ratesRes.ok) throw new Error(`rates lookup ${ratesRes.status}`)
    const rates = await ratesRes.json() as {
      zoneId: string
      tiers?: Array<{ id: string } & Record<string, unknown>>
    } | null
    if (!rates) throw new Error('No rates found for this zone')
    const restore = p.payload.restoreTiers
    const newTiers = Array.isArray(restore)
      ? (restore as Array<{ id: string }>)
      : (rates.tiers ?? []).filter(t => !String(t.id).startsWith(idPrefix))
    const updated = { ...rates, tiers: newTiers }
    const res = await callApi(`/api/rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(updated),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'rate.tod_surge_removed',
      resourceType: 'rate', resourceId: zoneId,
      resourceName: String(p.payload.zoneName ?? zoneId),
      detail: `Time-of-day surge removed (prefix ${idPrefix}) via Avery proposal`,
      averyStamp: averyStamp('remove_time_of_day_rate', { zoneId }, p),
    }, authHeaders())
  } else if (p.kind === 'cancel_subscription') {
    // Always cancel-at-period-end (immediate is never proposed by Avery).
    const subId = String(p.payload.subId)
    const res = await callApi(`/api/subscriptions/${encodeURIComponent(subId)}/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify({ immediate: false }),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'subscription.canceled_at_period_end',
      resourceType: 'subscription', resourceId: subId,
      resourceName: `${String(p.payload.plate ?? '')} · ${String(p.payload.planLabel ?? '')}`,
      detail: `Cancel at period end (${String(p.payload.currentPeriodEnd ?? 'period end')}) via Avery proposal`,
      // Monthly-recurring cents: before = current $/mo, after = 0 at period end.
      averyStamp: averyStamp('cancel_subscription', { zoneId: String(p.payload.zoneId ?? ''), before: Number(p.payload.amountCents), after: 0 }, p),
    }, authHeaders())
  } else if (p.kind === 'change_subscription_plan') {
    const { executeMonthlyPlanChange } = await import('./executeMonthlyPlanChange')
    await executeMonthlyPlanChange(p, authHeaders, cardKey, operator, averyStamp)
  } else if (p.kind === 'set_base_rate') {
    // Set the zone's FIRST base rate (Avery conversational rate-authoring, Phase
    // 1). Client fallback for when server apply is off — normally this executes
    // SERVER-side (it's in SERVER_APPLY_KINDS, which re-floors + drift-checks +
    // locks). DRIFT-SAFE here too: re-read the live record and REFUSE if an
    // always-on base rate appeared since the proposal, rather than adding a
    // second, ambiguous base tier. Works even if the zone has no rate record yet.
    const zoneId = String(p.payload.zoneId)
    const type = p.payload.type === 'flat' ? 'flat' : 'hourly'
    const tierId = String(p.payload.tierId ?? `${zoneId}_base`)
    const amountCents = Number(p.payload.amountCents)
    if (!zoneId || !Number.isFinite(amountCents)) throw new Error('Malformed base-rate proposal')
    const ratesRes = await callApi(`/api/rates?zoneId=${encodeURIComponent(zoneId)}`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!ratesRes.ok) throw new Error(`rates lookup ${ratesRes.status}`)
    const existing = await ratesRes.json() as {
      zoneId?: string
      tiers?: Array<{ id: string; type: string; amountCents: number; daysOfWeek?: number[]; timeFrom?: string; timeTo?: string }>
    } | null
    const base = existing ?? { zoneId, tiers: [] }
    const tiers = base.tiers ?? []
    const hasBase = tiers.some(t =>
      (t.type === 'flat' || t.type === 'hourly') && t.amountCents > 0 &&
      (!t.daysOfWeek || t.daysOfWeek.length === 0) && !t.timeFrom && !t.timeTo)
    if (hasBase) throw new Error('This zone already has a base rate — ask Avery for a fresh proposal.')
    if (type === 'flat' && tiers.some(t => t.type === 'graduated')) {
      throw new Error('This zone runs a graduated schedule; a flat base would override it. Ask Avery for a fresh proposal.')
    }
    const newTier = { id: tierId, type, amountCents: Math.round(amountCents) }
    const rawMin = p.payload.minDurationMinutes
    const minDur = typeof rawMin === 'number' && Number.isFinite(rawMin) ? Math.max(0, Math.round(rawMin)) : 0
    const updated = { ...base, zoneId, tiers: [...tiers, newTier], ...(minDur > 0 ? { minDurationMinutes: minDur } : {}) }
    const res = await callApi(`/api/rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(updated),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'rate.base_set',
      resourceType: 'rate', resourceId: `${zoneId}:${tierId}`,
      resourceName: String(p.payload.zoneName ?? zoneId),
      detail: `First base rate set: ${type} ${Math.round(amountCents)} cents${minDur > 0 ? ` (min ${minDur}m)` : ''} via Avery proposal`,
      // Brand-new base — no prior comparable, so after-only (delta null).
      averyStamp: averyStamp('set_base_rate', { zoneId, after: Math.round(amountCents) }, p),
    }, authHeaders())
  } else if (p.kind === 'set_graduated_schedule') {
    // Replace the zone's graduated tier (at most one applies) with the
    // proposed schedule. Works even if the zone has no rate record yet.
    const zoneId = String(p.payload.zoneId)
    const tier = p.payload.tier as ({ id: string; type: string } & Record<string, unknown>) | undefined
    if (!tier || !tier.id) throw new Error('Malformed graduated proposal')
    const ratesRes = await callApi(`/api/rates?zoneId=${encodeURIComponent(zoneId)}`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!ratesRes.ok) throw new Error(`rates lookup ${ratesRes.status}`)
    const existing = await ratesRes.json() as { zoneId?: string; tiers?: Array<{ id: string; type: string } & Record<string, unknown>> } | null
    const base = existing ?? { zoneId, tiers: [] }
    const kept = (base.tiers ?? []).filter(t => t.type !== 'graduated')
    const updated = { ...base, zoneId, tiers: [...kept, tier] }
    const res = await callApi(`/api/rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(updated),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'rate.graduated_set',
      resourceType: 'rate', resourceId: zoneId,
      resourceName: String(p.payload.zoneName ?? zoneId),
      detail: `Graduated schedule: ${String(p.payload.scheduleLabel ?? '')} via Avery proposal`,
      // Schedule change — no single before/after amount (delta null).
      averyStamp: averyStamp('set_graduated_schedule', { zoneId }, p),
    }, authHeaders())
  } else if (p.kind === 'update_zone_fees') {
    // Merge the proposed customerFees into the full rates record and POST back.
    const zoneId = String(p.payload.zoneId)
    const customerFees = p.payload.customerFees as Record<string, unknown>
    if (!zoneId || !customerFees) throw new Error('Malformed zone-fees proposal')
    const ratesRes = await callApi(`/api/rates?zoneId=${encodeURIComponent(zoneId)}`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!ratesRes.ok) throw new Error(`rates lookup ${ratesRes.status}`)
    const existing = await ratesRes.json() as ({ zoneId: string } & Record<string, unknown>) | null
    const base = existing ?? { zoneId, tiers: [] }
    const updated = { ...base, customerFees }
    const res = await callApi(`/api/rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(updated),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'rate.fees_updated',
      resourceType: 'rate', resourceId: zoneId,
      resourceName: zoneId,
      detail: `Zone fee config updated via Avery proposal: ${JSON.stringify(customerFees)}`,
      // Fee-config change — parking-revenue baseline doesn't measure it (delta null).
      averyStamp: averyStamp('update_zone_fees', { zoneId }, p),
    }, authHeaders())
  } else if (p.kind === 'update_fee_model') {
    // Parkify's revenue split, written at whichever tier the proposal names.
    // Each branch POSTs the SAME endpoint the corresponding ops fee card calls,
    // so Avery and the console can't drift into two different write contracts.
    // `feeModelConfig: null` is meaningful — it clears the tier's override so it
    // inherits again — which is why this reads the value rather than truthiness.
    const tier = String(p.payload.scope ?? '')
    const targetId = String(p.payload.targetId ?? '')
    const targetName = String(p.payload.targetName ?? targetId)
    const cfg = (p.payload.feeModelConfig ?? null) as Record<string, unknown> | null
    const expectedUpdatedAt = typeof p.payload.expectedUpdatedAt === 'string' ? p.payload.expectedUpdatedAt : undefined
    if (!targetId || (tier !== 'client' && tier !== 'facility' && tier !== 'zone')) {
      throw new Error('Malformed fee-model proposal')
    }
    if (tier === 'zone') {
      // Zones have no partial-upsert path — putRates stores the body verbatim,
      // so read the full rates record and merge, same as update_zone_fees.
      const ratesRes = await callApi(`/api/rates?zoneId=${encodeURIComponent(targetId)}`, {
        headers: authHeaders() as Record<string, string>,
      })
      if (!ratesRes.ok) throw new Error(`rates lookup ${ratesRes.status}`)
      const existing = await ratesRes.json() as ({ zoneId: string } & Record<string, unknown>) | null
      const base = existing ?? { zoneId: targetId, tiers: [] }
      // Clearing must DELETE the key — `feeModelConfig: null` would be stored
      // verbatim and then read back as a present-but-null config.
      const updated: Record<string, unknown> = { ...base }
      if (cfg === null) delete updated.feeModelConfig
      else updated.feeModelConfig = cfg
      const res = await callApi(`/api/rates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
        body: JSON.stringify({ ...updated, expectedUpdatedAt }),
      })
      if (!res.ok) throw new Error(await res.text())
    } else {
      // Client + location both take a partial upsert keyed by id — the server
      // carries every unsent field through from the stored record, and `null`
      // clears. Exactly the body ParkifyAdmin's saveClientFeeModel and
      // FacilitiesManager's handleSaveFees send.
      const path = tier === 'client' ? '/api/clients' : '/api/facilities'
      const res = await callApi(`${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
        body: JSON.stringify({ id: targetId, name: targetName, feeModelConfig: cfg, expectedUpdatedAt }),
      })
      if (!res.ok) throw new Error(await res.text())
    }
    void logAudit({
      action: `${tier}.fee_model_updated`,
      resourceType: tier, resourceId: targetId,
      resourceName: targetName,
      detail: cfg === null
        ? `Cleared the fee-model override on ${tier} ${targetName} via Avery proposal`
        : `Fee model set on ${tier} ${targetName} via Avery proposal: ${JSON.stringify(cfg)}`,
      // Config change — the parking-revenue baseline doesn't measure it.
      averyStamp: averyStamp('update_fee_model', tier === 'zone' ? { zoneId: targetId } : undefined, p),
    }, authHeaders())
  } else if (p.kind === 'set_contract_terms') {
    // Client-tier, same write contract as update_fee_model's client branch:
    // POST /api/clients is a partial upsert keyed by id — unsent fields carry
    // through from the stored record, so we only send what this proposal
    // actually carries. platformFeeBps is PARKIFY's take rate (money-adjacent,
    // hence the amber card); the rest is informational contract bookkeeping.
    const clientId = String(p.payload.clientId ?? '')
    const clientName = String(p.payload.clientName ?? clientId)
    if (!clientId) throw new Error('Malformed contract-terms proposal (no clientId)')
    // `!= null` (not `!== undefined`): this kind has no clear-via-null semantics
    // like update_fee_model above — a future producer (e.g. a PDF extraction
    // that couldn't read a field) sending explicit `null` should be treated as
    // "nothing to say here", not as clearing an existing value.
    const body: Record<string, unknown> = { id: clientId, name: clientName }
    if (p.payload.contractId != null) body.contractId = p.payload.contractId
    if (p.payload.source != null) body.contractSource = p.payload.source
    if (p.payload.platformFeeBps != null) body.platformFeeBps = p.payload.platformFeeBps
    if (p.payload.contractTerm != null) body.contractTerm = p.payload.contractTerm
    if (p.payload.contractTermDescription != null) body.contractTermDescription = p.payload.contractTermDescription
    if (p.payload.contractStartDate != null) body.contractStartDate = p.payload.contractStartDate
    if (p.payload.monthlyFeeUsd != null) body.contractMonthlyFeeUsd = p.payload.monthlyFeeUsd
    const res = await callApi(`/api/clients`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(body),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'client.contract_terms_applied',
      resourceType: 'client', resourceId: clientId,
      resourceName: clientName,
      detail: `Contract terms applied via Avery proposal (source: ${String(p.payload.source ?? 'unknown')}): ${JSON.stringify(body)}`,
      averyStamp: averyStamp('set_contract_terms', undefined, p),
    }, authHeaders())
  } else if (p.kind === 'set_min_duration') {
    // Merge the minimum-duration floor into the full rates record and POST back.
    // 0 removes the floor (putRates coerces; the resolver treats 0/absent the
    // same — no minimum). Works even if the zone has no rate record yet.
    const zoneId = String(p.payload.zoneId)
    const minDurationMinutes = Number(p.payload.minDurationMinutes)
    if (!zoneId || !Number.isFinite(minDurationMinutes)) throw new Error('Malformed min-duration proposal')
    const ratesRes = await callApi(`/api/rates?zoneId=${encodeURIComponent(zoneId)}`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!ratesRes.ok) throw new Error(`rates lookup ${ratesRes.status}`)
    const existing = await ratesRes.json() as ({ zoneId: string } & Record<string, unknown>) | null
    const base = existing ?? { zoneId, tiers: [] }
    const updated = { ...base, minDurationMinutes }
    const res = await callApi(`/api/rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(updated),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'rate.min_duration_set',
      resourceType: 'rate', resourceId: zoneId,
      resourceName: String(p.payload.zoneName ?? zoneId),
      detail: `Minimum duration set to ${minDurationMinutes} min via Avery proposal`,
      // Constraint change — no single before/after amount (delta null).
      averyStamp: averyStamp('set_min_duration', { zoneId }, p),
    }, authHeaders())
  } else if (p.kind === 'set_duration_ladder') {
    // The zone's checkout duration list. clear:true drops the key so checkout
    // falls back to the default list; putRates re-validates the list
    // (durationLadderError). Normally this executes SERVER-side (it's in
    // SERVER_APPLY_KINDS, which also drift-checks the snapshot), so this branch
    // only runs for a proposal with no server identity (store flag off).
    const zoneId = String(p.payload.zoneId ?? '')
    const clear = p.payload.clear === true
    const next = p.payload.durationLadderMinutes
    if (!zoneId || (!clear && !Array.isArray(next))) throw new Error('Malformed duration-list proposal')
    const ratesRes = await callApi(`/api/rates?zoneId=${encodeURIComponent(zoneId)}`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!ratesRes.ok) throw new Error(`rates lookup ${ratesRes.status}`)
    const existing = await ratesRes.json() as ({ zoneId: string; durationLadderMinutes?: number[] } & Record<string, unknown>) | null
    const { durationLadderMinutes: _prior, ...rest } = existing ?? { zoneId, tiers: [] }
    void _prior
    const updated = clear ? rest : { ...rest, durationLadderMinutes: next }
    const res = await callApi(`/api/rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(updated),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'rate.duration_ladder_set',
      resourceType: 'rate', resourceId: zoneId,
      resourceName: String(p.payload.zoneName ?? zoneId),
      detail: clear
        ? 'Checkout durations reset to the default list via Avery proposal'
        : `Checkout durations set to [${(next as unknown[]).join(', ')}] min via Avery proposal`,
      // Constraint change, no single before/after amount (delta null).
      averyStamp: averyStamp('set_duration_ladder', { zoneId }, p),
    }, authHeaders())
  } else if (p.kind === 'set_rate_product') {
    // Windowed fixed Blocks rows (api/src/avery/rate_products.ts). Normally this
    // executes SERVER-side (SERVER_APPLY_KINDS), which re-plans against the live
    // record. This branch only runs for a proposal with no server identity, so
    // it applies the planned tier list, and only if no rate row changed since
    // the proposal; putRates re-runs every overlap and choice check.
    const zoneId = String(p.payload.zoneId ?? '')
    const prior = p.payload.priorTiers
    const planned = p.payload.tiers
    const plannedAt = String(p.payload.plannedAt ?? '')
    if (!zoneId || !Array.isArray(prior) || !Array.isArray(planned) || !plannedAt) {
      throw new Error('Malformed rate-product proposal')
    }
    const ratesRes = await callApi(`/api/rates?zoneId=${encodeURIComponent(zoneId)}`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!ratesRes.ok) throw new Error(`rates lookup ${ratesRes.status}`)
    const existing = await ratesRes.json() as ({ zoneId: string; tiers?: unknown[] } & Record<string, unknown>) | null
    if (JSON.stringify(existing?.tiers ?? []) !== JSON.stringify(prior)) {
      throw new Error("This zone's rates changed since the proposal. Re-ask Avery for a fresh proposal.")
    }
    // A deactivation or reactivation the plan made carries the propose-time
    // instant; the change actually takes effect now.
    const executedAt = new Date().toISOString()
    type Period = { startsAt?: string | null; endsAt?: string | null }
    const tiers = (planned as Array<{ commercialPeriods?: Period[] } & Record<string, unknown>>).map(tier =>
      Array.isArray(tier.commercialPeriods)
        ? {
            ...tier,
            commercialPeriods: tier.commercialPeriods.map(period => ({
              ...period,
              ...(period.startsAt === plannedAt ? { startsAt: executedAt } : {}),
              ...(period.endsAt === plannedAt ? { endsAt: executedAt } : {}),
            })),
          }
        : tier)
    const res = await callApi(`/api/rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify({ ...(existing ?? {}), zoneId, tiers }),
    })
    if (!res.ok) throw new Error(await res.text())
    const retired = Array.isArray(p.payload.retired)
      ? (p.payload.retired as Array<{ label?: string }>).map(r => `"${String(r.label ?? '')}"`).join(', ')
      : ''
    void logAudit({
      action: 'rate.product_set',
      resourceType: 'rate', resourceId: zoneId,
      resourceName: String(p.payload.zoneName ?? zoneId),
      detail: `Rate products set: ${String(p.payload.label ?? '')}${retired ? `; deactivated ${retired}` : ''} via Avery proposal`,
      // Several prices at once, no single before/after amount (delta null).
      averyStamp: averyStamp('set_rate_product', { zoneId }, p),
    }, authHeaders())
  } else if (p.kind === 'set_daily_max') {
    // Merge dailyMaxCents into the full rates record and POST back. 0 removes
    // the cap (putRates coerces; the resolver treats 0/absent the same — no
    // cap). Works even if the zone has no rate record yet.
    const zoneId = String(p.payload.zoneId)
    const dailyMaxCents = Number(p.payload.dailyMaxCents)
    if (!zoneId || !Number.isFinite(dailyMaxCents)) throw new Error('Malformed daily-max proposal')
    const ratesRes = await callApi(`/api/rates?zoneId=${encodeURIComponent(zoneId)}`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!ratesRes.ok) throw new Error(`rates lookup ${ratesRes.status}`)
    const existing = await ratesRes.json() as ({ zoneId: string } & Record<string, unknown>) | null
    const base = existing ?? { zoneId, tiers: [] }
    const updated = { ...base, dailyMaxCents }
    const res = await callApi(`/api/rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(updated),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'rate.daily_max_set',
      resourceType: 'rate', resourceId: zoneId,
      resourceName: String(p.payload.zoneName ?? zoneId),
      detail: `Daily max ${String(p.payload.currentDailyMaxCents ?? 0)} → ${dailyMaxCents} cents via Avery proposal`,
      averyStamp: averyStamp('set_daily_max', { zoneId, before: Number(p.payload.currentDailyMaxCents ?? 0), after: dailyMaxCents }, p),
    }, authHeaders())
  } else if (p.kind === 'set_monthly_plan') {
    // Upsert one MonthlyPlan by id into rates.monthlyPlans and POST the full
    // record back. Works even if the zone has no rate record yet (a brand-new
    // plan on an unpriced zone is a valid state — base pricing is separate).
    const zoneId = String(p.payload.zoneId)
    const planId = String(p.payload.planId)
    const label = String(p.payload.label)
    const amountCents = Number(p.payload.amountCents)
    if (!zoneId || !planId || !label || !Number.isFinite(amountCents)) throw new Error('Malformed monthly-plan proposal')
    const ratesRes = await callApi(`/api/rates?zoneId=${encodeURIComponent(zoneId)}`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!ratesRes.ok) throw new Error(`rates lookup ${ratesRes.status}`)
    const existing = await ratesRes.json() as ({ zoneId: string; monthlyPlans?: Array<{ id: string; label: string; amountCents: number; description?: string; active?: boolean }> } & Record<string, unknown>) | null
    const base = existing ?? { zoneId, tiers: [] }
    const plans = base.monthlyPlans ?? []
    const isNew = Boolean(p.payload.isNew)
    const nextPlan = {
      id: planId,
      label,
      amountCents,
      ...(p.payload.description !== undefined ? { description: String(p.payload.description) } : {}),
      ...(p.payload.active !== undefined ? { active: Boolean(p.payload.active) } : {}),
    }
    const updatedPlans = isNew
      ? [...plans, nextPlan]
      : plans.map(pl => pl.id === planId ? { ...pl, ...nextPlan } : pl)
    const updated = { ...base, zoneId, monthlyPlans: updatedPlans }
    const res = await callApi(`/api/rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(updated),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: isNew ? 'rate.monthly_plan_created' : 'rate.monthly_plan_updated',
      resourceType: 'rate.monthly_plan', resourceId: `${zoneId}:${planId}`,
      resourceName: `${String(p.payload.zoneName ?? zoneId)} · ${label}`,
      detail: `${isNew ? 'Created' : `${String(p.payload.currentAmountCents ?? amountCents)} → ${amountCents}`} cents/mo via Avery proposal`,
      averyStamp: averyStamp('set_monthly_plan', { zoneId, before: isNew ? null : Number(p.payload.currentAmountCents ?? amountCents), after: amountCents }, p),
    }, authHeaders())
  } else if (p.kind === 'extend_session') {
    // MONEY-MOVING: charges the parker's saved card off-session. Goes through the
    // tenant-scoped, permission-gated operator route (extend.session) — NOT the
    // customer HMAC/portal path. The Worker re-verifies tenant ownership and is the
    // authoritative idempotency + charge guard; we send a STABLE per-card key so a
    // Retry can never produce a second charge.
    const txnId = String(p.payload.txnId ?? '')
    const additionalMinutes = Number(p.payload.additionalMinutes)
    if (!txnId || !Number.isFinite(additionalMinutes) || additionalMinutes <= 0) {
      throw new Error('Malformed extend proposal')
    }
    // No saved card → the operator route returns 409. Surface the clearer message
    // up front rather than firing a charge that can't run off-session.
    if (p.payload.hasSavedCard === false) {
      throw new Error('This session has no saved card — send the customer the extend link to add time.')
    }
    const idempotencyKey = extendIdempotencyKey(cardKey)
    const res = await callApi(`/api/sessions/extend/operator`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify({ txnId, additionalMinutes, idempotencyKey }),
    })
    if (!res.ok) throw new Error(await res.text())
    const data = await res.json() as { chargedCents?: number; newExpiresAt?: string; stripeIntentId?: string | null }
    const zoneId = String(p.payload.zoneId ?? '')
    const chargedCents = Number(data.chargedCents ?? p.payload.chargeCents ?? 0)
    void logAudit({
      action: 'session.extended',
      resourceType: 'session', resourceId: txnId,
      resourceName: `${String(p.payload.plate ?? '')} · ${zoneId}`,
      detail: `+${additionalMinutes} min · ${fmtMoney(chargedCents)}` +
        (data.stripeIntentId ? ` (${data.stripeIntentId})` : ' (no charge — daily max reached)') +
        ` · now expires ${data.newExpiresAt ?? '—'} — extended via Avery proposal`,
      // The authoritative before/after/delta lives on the SERVER stamp written by
      // extendSession({ stamp: true }); the client stamp records operator attribution
      // only (delta null) so the revenue baseline doesn't double-count the charge.
      averyStamp: averyStamp('extend_session', { zoneId }, p),
    }, authHeaders())
  } else if (p.kind === 'create_client') {
    // Onboarding step 1. Mirrors OnboardClientWizard's saveClient exactly: no
    // `id` in the body (upsertClient slugifies the name server-side, same as
    // create_zone above omitting `id` for uid('zone')).
    const name = String(p.payload.name ?? '')
    const res = await callApi(`/api/clients`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(p.payload),
    })
    if (!res.ok) throw new Error(await res.text())
    const created = await res.json() as { id?: string }
    void logAudit({
      action: 'client.created',
      resourceType: 'client', resourceId: String(created.id ?? name),
      resourceName: name,
      detail: `Created client "${name}" via Avery proposal`,
      averyStamp: averyStamp('create_client', undefined, p),
    }, authHeaders())
  } else if (p.kind === 'invite_team_member') {
    // Onboarding step 3. Mirrors OnboardClientWizard's saveMember: save the
    // membership, then best-effort the invite email — a failed email doesn't
    // undo the save (matches the wizard's per-row "Saved, but the invite email
    // failed" handling, though here it surfaces as this proposal's own error).
    const { clientName, ...body } = p.payload
    const zoneIds = Array.isArray(p.payload.zoneIds) ? p.payload.zoneIds : []
    const res = await callApi(`/api/client/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify({ ...body, zoneIds, active: true }),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'team.invited',
      resourceType: 'user', resourceId: String(p.payload.email ?? ''),
      resourceName: String(p.payload.name ?? p.payload.email ?? ''),
      detail: `role: ${String(p.payload.role ?? '')}, client: ${String(p.payload.clientId ?? '')} — invited via Avery proposal`,
      averyStamp: averyStamp('invite_team_member', undefined, p),
    }, authHeaders())
    // Best-effort — same contract as the wizard: a failed send doesn't undo
    // the membership just saved, so this is fire-and-forget, not awaited/thrown.
    // Best-effort is NOT silent, though: sendInviteEmail reports every failure
    // (see below), because "member saved" and "member emailed" are two facts and
    // the card only ever showed the first.
    //
    // `invitedBy` attributes the invite to the APPROVING OPERATOR, not to Avery.
    // Two reasons to stay on the operator: the audit entry stamped just above
    // records the actor from this request's token (the person) with the
    // averyStamp as provenance, so an Avery attribution here would contradict
    // it; and the handler renders the value into the subject line ("X invited
    // you to Parkify") and stores it as actorEmail on its own invite.sent event,
    // both of which want a person. Matches the other four call sites
    // (ClientAdmin, ZoneTeamTab, ParkifyAdmin, OnboardClientWizard).
    void sendInviteEmail({
      email: String(p.payload.email ?? ''),
      role: String(p.payload.role ?? ''),
      invitedBy: operator?.name ?? operator?.email ?? '',
      name: typeof p.payload.name === 'string' ? p.payload.name : undefined,
      companyName: typeof clientName === 'string' ? clientName : undefined,
      clientId: typeof p.payload.clientId === 'string' ? p.payload.clientId : null,
    }, authHeaders)
  } else if (p.kind === 'send_payout_link') {
    // Onboarding step 4. `to`/`method` were resolved from the CLIENT RECORD at
    // propose time (proposeSendPayoutLink), never typed by the model — this
    // call just fires the same endpoint the wizard's PayoutsStep button does.
    const clientId = String(p.payload.clientId ?? '')
    const res = await callApi(`/api/clients/${encodeURIComponent(clientId)}/stripe/send-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify({ method: p.payload.method, to: p.payload.to }),
    })
    if (!res.ok) throw new Error(await res.text())
    void logAudit({
      action: 'client.payout_link_sent',
      resourceType: 'client', resourceId: clientId,
      resourceName: String(p.payload.clientName ?? clientId),
      detail: `Payout-setup link sent via ${String(p.payload.method ?? '')} to ${String(p.payload.to ?? '')} — via Avery proposal`,
      averyStamp: averyStamp('send_payout_link', undefined, p),
    }, authHeaders())
  } else {
    // Deploy skew: the Worker proposed a kind this bundle doesn't know how to
    // execute. ProposalCard now renders such a card (unknown-kind fallback) and
    // makes it clickable, so we MUST fail closed here — throw a clear error
    // rather than falling through and resolving as a false "✓ Done" with no
    // action taken and no audit event written.
    throw new Error(
      `This Parkify build can't execute a "${String((p as { kind: string }).kind)}" action yet — refresh to update, or handle it manually.`,
    )
  }
}

// ─── Invite email (fire-and-forget, but never silent) ───────────────────────

/**
 * POST the invite email for a saved membership.
 *
 * Deliberately not awaited by the caller: the membership is already saved and a
 * failed email must not undo it or fail the card. What it must NOT do is
 * disappear. The original `.catch(() => {})` here meant a malformed body — this
 * function's own caller was omitting the required `invitedBy` — returned 400 on
 * every single Avery-driven invite, and the operator saw an unqualified "✓ Done"
 * on a person who was never emailed. Any future breakage of this send (a new
 * required field, a tenancy refusal, Resend down) would have been just as
 * invisible, which is the part actually worth fixing.
 *
 * The pre-flight check is the same list the Worker's guard filters on, so a
 * request that cannot succeed is reported as the caller's bug instead of being
 * spent on the network and read back as an email outage.
 */
async function sendInviteEmail(invite: InviteRequest, authHeaders: () => HeadersInit): Promise<void> {
  const missing = REQUIRED_INVITE_FIELDS.filter(f => !invite[f])
  if (missing.length) {
    reportInviteFailure(invite.email, `the request was missing ${missing.join(', ')}`)
    return
  }
  try {
    const res = await callApi(`/api/notifications/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
      body: JSON.stringify(invite),
    })
    if (!res.ok) {
      reportInviteFailure(invite.email, `${res.status} ${(await res.text().catch(() => '')).slice(0, 200)}`.trim())
    }
  } catch (e) {
    reportInviteFailure(invite.email, e instanceof Error ? e.message : String(e))
  }
}

/**
 * Tell the operator the email didn't go out — non-blocking and after the fact,
 * mirroring ClientAdmin's own handling of the same send. The membership stands;
 * the only thing being corrected is the belief that the person was notified,
 * which is what makes the difference between "resend it" and "wonder for a week
 * why they never signed in".
 */
function reportInviteFailure(email: string, detail: string): void {
  const message = `Member saved, but the invite email to ${email || '(no address)'} was not sent: ${detail}`
  console.error('[avery] invite email failed —', message)
  // Guarded: this module is exercised in a non-DOM test environment too.
  if (typeof window !== 'undefined' && typeof window.alert === 'function') window.alert(message)
}

